// Parse and format numbers the way an Austrian user types them.
//
// DE format: thousand separator = ".", decimal separator = ",".
// Users type "390.000" meaning 390 000 (three hundred ninety thousand),
// not 390 (the JS Number default interpretation).
//
// The formatters and the parser must round-trip: formatDeInteger seeds a price
// input and formatDeDecimal an area or percentage input from a saved row, and
// parseDeNumber reads either back on submit. See parseDeNumber.test.ts — the
// round-trip tests are the ones that matter.
//
// They drifted apart once, with consequences: formatDeInteger delegated to
// toLocaleString('de-AT'), and ICU groups plain numbers in Austrian German with
// U+00A0 ("390 000") even though it groups *currency* with dots ("€ 390.000").
// parseDeNumber only stripped dots, so the round trip returned null and
// EditPropertyDialog submitted purchasePrice: null on every save where the user
// had not retyped the price. 54 wipes across 48 properties in production
// between 2026-05-10 and 2026-08-18 before this was found.
//
// The lesson encoded here: never derive one half of a round trip from ICU while
// the other half is hand-written. Grouping is now explicit.
//
// It happened again with areas: EditPropertyDialog seeded Wohnfläche and
// Grundfläche with String(n), so 50.37 m² became "50.37" and came back as 5037
// on every save. Decimal inputs are seeded with formatDeDecimal.

/** Characters that may appear as a thousand separator in pasted input, plus the
 *  currency symbol. Excel, portal listings and iOS keyboards all differ. */
const NOISE = /[\s    €]/g;

/** A lone dot with no comma. The capture is the part after the dot, whose
 *  length decides what the dot means. */
const SINGLE_DOT = /^-?\d*\.(\d+)$/;

export function parseDeNumber(input: string | null | undefined): number | null {
  if (input == null) return null;
  const trimmed = String(input).trim();
  if (trimmed === '') return null;
  // Strip spaces and currency first, so "€ 390 000" and "390.000" both work.
  const bare = trimmed.replace(NOISE, '');
  // A thousand group always has exactly three digits, so a single dot followed
  // by any other number of digits is a decimal point someone typed out of
  // habit: "43.69" is 43,69 m², never 4369. Exactly three stays a separator,
  // because that is what "1.250" means in Austrian German.
  const dot = SINGLE_DOT.exec(bare);
  const cleaned =
    dot && dot[1]!.length !== 3 ? bare : bare.replace(/\./g, '').replace(/,/g, '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n;
}

/**
 * Format a whole number with Austrian thousand dots ("390.000").
 *
 * Grouping is applied explicitly rather than through toLocaleString, because
 * the value this produces is fed straight back into parseDeNumber. Returns ''
 * for null / undefined / NaN — convenient for re-seeding an input from a row.
 */
export function formatDeInteger(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const rounded = Math.round(Number(n));
  const [sign, digits] = rounded < 0 ? ['-', String(-rounded)] : ['', String(rounded)];
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Format any number with an Austrian decimal comma and no grouping ("50,37").
 *
 * For inputs that hold areas, percentages and other measurements, which
 * parseDeNumber reads back on submit. The output never contains a dot:
 * String(n) is the shortest text that reads back as exactly n, and swapping its
 * one decimal point for a comma is all parseDeNumber needs. Grouping is left out
 * on purpose, because a year like 1925 must not come out as "1.925". Returns ''
 * for null / undefined / NaN / Infinity.
 */
export function formatDeDecimal(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return '';
  return String(Number(n)).replace('.', ',');
}
